import type {
  AddCaseRequestCommentPayload,
  CaseRequestCommunicationMessage,
  CaseRequestCommunicationsResponse,
  CaseRequestCommunicationsThread,
  CaseRequestCommunicationMessageResponse,
  CommunicationHoldStatusAction,
} from '../types/caseRequestCommunications';
import { caseApiRequest, dataPayloadErrorMessage, isDataPayloadSuccess } from './apiClient';
import type { ApiResult } from './types';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function commStrField(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function normalizeMessage(raw: unknown, index: number): CaseRequestCommunicationMessage {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const visible = r.visible_to_external_agency ?? r.visibleToExternalAgency;
  return {
    id: commStrField(r, 'id') || `msg-${index}`,
    comment: commStrField(r, 'comment'),
    senderName: commStrField(r, 'sender_name', 'senderName'),
    senderSubtitle: commStrField(r, 'sender_subtitle', 'senderSubtitle'),
    role: commStrField(r, 'role'),
    agency: commStrField(r, 'agency'),
    intranet: r.intranet === true,
    createdAt: commStrField(r, 'created_at', 'createdAt'),
    visibleToExternalAgency: visible === true,
  };
}

function unwrapEnvelopeData(raw: unknown): Record<string, unknown> {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  if (r.data && typeof r.data === 'object' && !Array.isArray(r.data)) {
    return r.data as Record<string, unknown>;
  }
  return r;
}

function normalizeThread(caseRequestId: string, raw: unknown): CaseRequestCommunicationsThread {
  const r = unwrapEnvelopeData(raw);
  const messagesRaw = r.messages;
  const messages = Array.isArray(messagesRaw)
    ? messagesRaw.map((m, i) => normalizeMessage(m, i))
    : [];
  return {
    caseRequestId: commStrField(r, 'case_request_id', 'caseRequestId') || caseRequestId,
    messages,
  };
}

/** CMS-CRC-11 — communication thread for a case request */
export async function getCaseRequestCommunications(
  caseRequestId: string,
): Promise<ApiResult<CaseRequestCommunicationsThread>> {
  const id = caseRequestId.trim();
  if (!UUID_RE.test(id)) {
    return { success: false, message: 'Invalid case request id.' };
  }

  const res = await caseApiRequest<CaseRequestCommunicationsResponse>(
    `/${encodeURIComponent(id)}/communications`,
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeThread(id, res.data) };
}

/** CMS-CRC-12 — add a comment to the communication thread */
export async function addCaseRequestComment(
  caseRequestId: string,
  comment: string,
  options?: { visibleToExternalAgency?: boolean },
): Promise<ApiResult<CaseRequestCommunicationMessage>> {
  const id = caseRequestId.trim();
  const text = comment.trim();
  if (!UUID_RE.test(id)) {
    return { success: false, message: 'Invalid case request id.' };
  }
  if (!text) return { success: false, message: 'Comment is required.' };

  const body: AddCaseRequestCommentPayload = {
    comment: text,
    visible_to_external_agency: options?.visibleToExternalAgency ?? false,
  };

  const res = await caseApiRequest<CaseRequestCommunicationMessageResponse>(
    `/${encodeURIComponent(id)}/communications`,
    { method: 'POST', body },
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeMessage(res.data, 0) };
}

/** CMS — update current-stage communication hold status (On Hold / Resume). */
export async function updateCommunicationHoldStatus(
  caseRequestId: string,
  input: {
    action: CommunicationHoldStatusAction;
    visibleToExternalAgency: boolean;
    applicationCode?: string;
  },
): Promise<ApiResult<void>> {
  const id = caseRequestId.trim();
  if (!UUID_RE.test(id)) {
    return { success: false, message: 'Invalid case request id.' };
  }

  const applicationCode = (input.applicationCode ?? import.meta.env.VITE_APP_CODE_COMM_HOLD_STATUS ?? 'INTRANET').trim();
  const body = {
    action: input.action,
    visible_to_external_agency: input.visibleToExternalAgency,
  };

  const res = await caseApiRequest<unknown>(
    `/${encodeURIComponent(id)}/communications/current-stage/hold-status`,
    {
      method: 'POST',
      headers: { applicationCode, 'application-code': applicationCode },
      body,
    },
  );
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) return { success: false, message: dataPayloadErrorMessage(res.data) };
  return { success: true, data: undefined };
}
